/**
 * StepConfigureEscalation — Third onboarding step: urgency tiers & escalation.
 *
 * Configures blocking/clarification/advisory tiers, sleep hours, channel preferences.
 * Calls POST /api/onboarding/wizard/escalation.
 */
import { useState, type FormEvent } from "react";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Select } from "@/components/ui/Select";
import { Badge } from "@/components/ui/Badge";
import { api } from "@/lib/api";

interface TierConfig {
  enabled: boolean;
  preferredChannel: string;
}

interface StepConfigureEscalationProps {
  orgId: string;
  onNext(): void;
  onSkip(): void;
}

export function StepConfigureEscalation({
  orgId,
  onNext,
  onSkip,
}: StepConfigureEscalationProps) {
  const [blocking, setBlocking] = useState<TierConfig>({
    enabled: true,
    preferredChannel: "sms",
  });
  const [clarification, setClarification] = useState<TierConfig>({
    enabled: true,
    preferredChannel: "slack",
  });
  const [advisory, setAdvisory] = useState<TierConfig>({
    enabled: true,
    preferredChannel: "email",
  });
  const [sleepStart, setSleepStart] = useState("22:00");
  const [sleepEnd, setSleepEnd] = useState("08:00");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await api.post("/api/onboarding/wizard/escalation", {
        orgId,
        tiers: {
          blocking: { ...blocking },
          clarification: { ...clarification },
          advisory: { ...advisory },
        },
        sleepHours: {
          start: sleepStart,
          end: sleepEnd,
        },
      });
      onNext();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to configure escalation",
      );
    } finally {
      setIsLoading(false);
    }
  }

  const channelOptions = ["sms", "slack", "email", "telegram", "whatsapp"];

  function renderTier(
    label: string,
    variant: "danger" | "warning" | "primary",
    description: string,
    config: TierConfig,
    setConfig: (c: TierConfig) => void,
  ) {
    return (
      <div className="rounded-lg border border-white/10 bg-surface p-4">
        <div className="mb-3 flex items-center gap-2">
          <Badge variant={variant}>{label}</Badge>
          <span className="text-xs text-muted-foreground">{description}</span>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={config.enabled}
              onChange={(e) =>
                setConfig({ ...config, enabled: e.target.checked })
              }
              className="h-4 w-4 rounded border-white/20 bg-surface accent-primary"
            />
            Enabled
          </label>
          <Select
            label=""
            value={config.preferredChannel}
            onChange={(e) =>
              setConfig({ ...config, preferredChannel: e.target.value })
            }
            disabled={!config.enabled}
          >
            {channelOptions.map((ch) => (
              <option key={ch} value={ch}>
                {ch.charAt(0).toUpperCase() + ch.slice(1)}
              </option>
            ))}
          </Select>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <div>
        <h2 className="text-xl font-semibold text-white">
          Configure Escalation
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Set how urgent messages reach you on different channels.
        </p>
      </div>

      <div className="flex flex-col gap-4">
        {renderTier(
          "Blocking",
          "danger",
          "Agent is stuck and needs immediate help",
          blocking,
          setBlocking,
        )}
        {renderTier(
          "Clarification",
          "warning",
          "Agent has a question but can continue",
          clarification,
          setClarification,
        )}
        {renderTier(
          "Advisory",
          "primary",
          "FYI notifications, no action needed",
          advisory,
          setAdvisory,
        )}
      </div>

      <div className="rounded-lg border border-white/10 bg-surface p-4">
        <h3 className="mb-3 text-sm font-medium text-white">Sleep Hours</h3>
        <p className="mb-3 text-xs text-muted-foreground">
          During sleep hours, only blocking messages will trigger SMS. Others
          will be queued.
        </p>
        <div className="flex items-center gap-4">
          <Input
            label="Start"
            type="time"
            value={sleepStart}
            onChange={(e) => setSleepStart(e.target.value)}
          />
          <Input
            label="End"
            type="time"
            value={sleepEnd}
            onChange={(e) => setSleepEnd(e.target.value)}
          />
        </div>
      </div>

      {error && (
        <p className="text-sm text-danger" role="alert">
          {error}
        </p>
      )}

      <div className="flex gap-3">
        <Button type="submit" className="flex-1" loading={isLoading}>
          Save Escalation Config
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={onSkip}
          disabled={isLoading}
        >
          Skip for now
        </Button>
      </div>
    </form>
  );
}
